
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {IdempotentRequestState} from '../storage/IdempotentRequestState';
import {FieldModelIdempotentRequestState} from '../storage/IdempotentRequestState';

/**
 * IdempotencyInfoRecord struct
 */
class IdempotencyInfoRecord {
  /**
   * Initialize struct
   * @param {!string=} hash
   * @param {!string=} key
   * @param {!string=} api
   * @param {!number=} login
   * @param {!string=} serverId
   * @param {!string=} requestHash
   * @param {Uint8Array=} responseBlob
   * @param {!number=} responseFbeType
   * @param {!IdempotentRequestState=} state
   * @param {!Date=} created
   * @param {!Date=} updated
   * @param {!number=} ttlSeconds
   * @constructor
   */
  constructor (hash = '', key = '', api = '', login = new UInt64(0, 0), serverId = '', requestHash = '', responseBlob = null, responseFbeType = new UInt64(0, 0), state = new IdempotentRequestState(), created = new Date(0), updated = new Date(0), ttlSeconds = new UInt64(0, 0)) {
    this.Hash = hash
    this.Key = key
    this.Api = api
    this.Login = login
    this.ServerId = serverId
    this.RequestHash = requestHash
    this.ResponseBlob = responseBlob
    this.ResponseFbeType = responseFbeType
    this.State = state
    this.Created = created
    this.Updated = updated
    this.TtlSeconds = ttlSeconds
  }

  /**
   * Copy struct (shallow copy)
   * @this {!IdempotencyInfoRecord}
   * @param {!IdempotencyInfoRecord} other Other struct
   * @returns {!IdempotencyInfoRecord} This struct
   */
  copy (other) {
    if (other.Hash != null) {
      this.Hash = other.Hash
    } else {
      this.Hash = null
    }
    if (other.Key != null) {
      this.Key = other.Key
    } else {
      this.Key = null
    }
    if (other.Api != null) {
      this.Api = other.Api
    } else {
      this.Api = null
    }
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.ServerId != null) {
      this.ServerId = other.ServerId
    } else {
      this.ServerId = null
    }
    if (other.RequestHash != null) {
      this.RequestHash = other.RequestHash
    } else {
      this.RequestHash = null
    }
    if (other.ResponseBlob != null) {
      if (typeof other.ResponseBlob === 'string') {
        // noinspection JSUnresolvedFunction
        this.ResponseBlob = Uint8Array.from(Buffer.from(other.ResponseBlob, 'base64'))
      } else {
        this.ResponseBlob = Uint8Array.from(other.ResponseBlob)
      }
    } else {
      this.ResponseBlob = null
    }
    if (other.ResponseFbeType != null) {
      this.ResponseFbeType = UInt64.fromNumber(other.ResponseFbeType)
    } else {
      this.ResponseFbeType = null
    }
    if (other.State != null) {
      this.State = IdempotentRequestState.fromObject(other.State)
    } else {
      this.State = null
    }
    if (other.Created != null) {
      if (other.Created instanceof Date) {
        this.Created = new Date(other.Created.getTime())
      } else {
        this.Created = new Date(Math.round(other.Created / 1000000))
      }
    } else {
      this.Created = null
    }
    if (other.Updated != null) {
      if (other.Updated instanceof Date) {
        this.Updated = new Date(other.Updated.getTime())
      } else {
        this.Updated = new Date(Math.round(other.Updated / 1000000))
      }
    } else {
      this.Updated = null
    }
    if (other.TtlSeconds != null) {
      this.TtlSeconds = UInt64.fromNumber(other.TtlSeconds)
    } else {
      this.TtlSeconds = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!IdempotencyInfoRecord}
   * @returns {!IdempotencyInfoRecord} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new IdempotencyInfoRecordModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new IdempotencyInfoRecordModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!IdempotencyInfoRecord}
   * @param {!IdempotencyInfoRecord} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof IdempotencyInfoRecord)) {
      throw new TypeError('Instance of IdempotencyInfoRecord is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!IdempotencyInfoRecord}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Hash: ((this.Hash != null) ? this.Hash : null),
      Key: ((this.Key != null) ? this.Key : null),
      Api: ((this.Api != null) ? this.Api : null),
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      ServerId: ((this.ServerId != null) ? this.ServerId : null),
      RequestHash: ((this.RequestHash != null) ? this.RequestHash : null),
      ResponseBlob: ((this.ResponseBlob != null) ? Buffer.from(this.ResponseBlob).toString('base64') : null),
      ResponseFbeType: ((this.ResponseFbeType != null) ? this.ResponseFbeType.toNumber() : null),
      State: ((this.State != null) ? this.State : null),
      Created: ((this.Created != null) ? (this.Created.getTime() * 1000000) : null),
      Updated: ((this.Updated != null) ? (this.Updated.getTime() * 1000000) : null),
      TtlSeconds: ((this.TtlSeconds != null) ? this.TtlSeconds.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return IdempotencyInfoRecord.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!IdempotencyInfoRecord} other Object value
   * @returns {!IdempotencyInfoRecord} Created struct
   */
  static fromObject (other) {
    return new IdempotencyInfoRecord().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!IdempotencyInfoRecord}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return IdempotencyInfoRecord.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!IdempotencyInfoRecord}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4231
  }
}

export { IdempotencyInfoRecord };

/**
 * Fast Binary Encoding IdempotencyInfoRecord field model
 */
class FieldModelIdempotencyInfoRecord extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Hash = new fbe.FieldModelString(buffer, 4 + 4)
    this.Key = new fbe.FieldModelString(buffer, this.Hash.fbeOffset + this.Hash.fbeSize)
    this.Api = new fbe.FieldModelString(buffer, this.Key.fbeOffset + this.Key.fbeSize)
    this.Login = new fbe.FieldModelUInt64(buffer, this.Api.fbeOffset + this.Api.fbeSize)
    this.ServerId = new fbe.FieldModelString(buffer, this.Login.fbeOffset + this.Login.fbeSize)
    this.RequestHash = new fbe.FieldModelString(buffer, this.ServerId.fbeOffset + this.ServerId.fbeSize)
    this.ResponseBlob = new fbe.FieldModelOptional(new fbe.FieldModelBytes(buffer, this.RequestHash.fbeOffset + this.RequestHash.fbeSize), buffer, this.RequestHash.fbeOffset + this.RequestHash.fbeSize)
    this.ResponseFbeType = new fbe.FieldModelUInt64(buffer, this.ResponseBlob.fbeOffset + this.ResponseBlob.fbeSize)
    this.State = new FieldModelIdempotentRequestState(buffer, this.ResponseFbeType.fbeOffset + this.ResponseFbeType.fbeSize)
    this.Created = new fbe.FieldModelTimestamp(buffer, this.State.fbeOffset + this.State.fbeSize)
    this.Updated = new fbe.FieldModelTimestamp(buffer, this.Created.fbeOffset + this.Created.fbeSize)
    this.TtlSeconds = new fbe.FieldModelUInt64(buffer, this.Updated.fbeOffset + this.Updated.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelIdempotencyInfoRecord}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelIdempotencyInfoRecord}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Hash.fbeSize + this.Key.fbeSize + this.Api.fbeSize + this.Login.fbeSize + this.ServerId.fbeSize + this.RequestHash.fbeSize + this.ResponseBlob.fbeSize + this.ResponseFbeType.fbeSize + this.State.fbeSize + this.Created.fbeSize + this.Updated.fbeSize + this.TtlSeconds.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelIdempotencyInfoRecord}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Hash.fbeExtra + this.Key.fbeExtra + this.Api.fbeExtra + this.Login.fbeExtra + this.ServerId.fbeExtra + this.RequestHash.fbeExtra + this.ResponseBlob.fbeExtra + this.ResponseFbeType.fbeExtra + this.State.fbeExtra + this.Created.fbeExtra + this.Updated.fbeExtra + this.TtlSeconds.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelIdempotencyInfoRecord}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelIdempotencyInfoRecord.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelIdempotencyInfoRecord}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4231
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelIdempotencyInfoRecord}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelIdempotencyInfoRecord}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Hash.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Hash.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Hash.fbeSize

    if ((fbeCurrentSize + this.Key.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Key.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key.fbeSize

    if ((fbeCurrentSize + this.Api.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Api.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Api.fbeSize

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.ServerId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ServerId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ServerId.fbeSize

    if ((fbeCurrentSize + this.RequestHash.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RequestHash.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestHash.fbeSize

    if ((fbeCurrentSize + this.ResponseBlob.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ResponseBlob.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResponseBlob.fbeSize

    if ((fbeCurrentSize + this.ResponseFbeType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ResponseFbeType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResponseFbeType.fbeSize

    if ((fbeCurrentSize + this.State.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.State.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.State.fbeSize

    if ((fbeCurrentSize + this.Created.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Created.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Created.fbeSize

    if ((fbeCurrentSize + this.Updated.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Updated.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Updated.fbeSize

    if ((fbeCurrentSize + this.TtlSeconds.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TtlSeconds.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TtlSeconds.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelIdempotencyInfoRecord}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelIdempotencyInfoRecord}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelIdempotencyInfoRecord}
   * @param {!IdempotencyInfoRecord} fbeValue Default value, defaults is new IdempotencyInfoRecord()
   * @returns {!IdempotencyInfoRecord} IdempotencyInfoRecord value
   */
  get (fbeValue = new IdempotencyInfoRecord()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelIdempotencyInfoRecord}
   * @param {!IdempotencyInfoRecord} fbeValue IdempotencyInfoRecord value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Hash.fbeSize) <= fbeStructSize) {
      fbeValue.Hash = this.Hash.get()
    } else {
      fbeValue.Hash = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Hash.fbeSize

    if ((fbeCurrentSize + this.Key.fbeSize) <= fbeStructSize) {
      fbeValue.Key = this.Key.get()
    } else {
      fbeValue.Key = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Key.fbeSize

    if ((fbeCurrentSize + this.Api.fbeSize) <= fbeStructSize) {
      fbeValue.Api = this.Api.get()
    } else {
      fbeValue.Api = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Api.fbeSize

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.ServerId.fbeSize) <= fbeStructSize) {
      fbeValue.ServerId = this.ServerId.get()
    } else {
      fbeValue.ServerId = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ServerId.fbeSize

    if ((fbeCurrentSize + this.RequestHash.fbeSize) <= fbeStructSize) {
      fbeValue.RequestHash = this.RequestHash.get()
    } else {
      fbeValue.RequestHash = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RequestHash.fbeSize

    if ((fbeCurrentSize + this.ResponseBlob.fbeSize) <= fbeStructSize) {
      fbeValue.ResponseBlob = this.ResponseBlob.get()
    } else {
      fbeValue.ResponseBlob = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResponseBlob.fbeSize

    if ((fbeCurrentSize + this.ResponseFbeType.fbeSize) <= fbeStructSize) {
      fbeValue.ResponseFbeType = this.ResponseFbeType.get()
    } else {
      fbeValue.ResponseFbeType = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResponseFbeType.fbeSize

    if ((fbeCurrentSize + this.State.fbeSize) <= fbeStructSize) {
      fbeValue.State = this.State.get()
    } else {
      fbeValue.State = new IdempotentRequestState()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.State.fbeSize

    if ((fbeCurrentSize + this.Created.fbeSize) <= fbeStructSize) {
      fbeValue.Created = this.Created.get()
    } else {
      fbeValue.Created = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Created.fbeSize

    if ((fbeCurrentSize + this.Updated.fbeSize) <= fbeStructSize) {
      fbeValue.Updated = this.Updated.get()
    } else {
      fbeValue.Updated = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Updated.fbeSize

    if ((fbeCurrentSize + this.TtlSeconds.fbeSize) <= fbeStructSize) {
      fbeValue.TtlSeconds = this.TtlSeconds.get()
    } else {
      fbeValue.TtlSeconds = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TtlSeconds.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelIdempotencyInfoRecord}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelIdempotencyInfoRecord}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelIdempotencyInfoRecord}
   * @param {!IdempotencyInfoRecord} fbeValue IdempotencyInfoRecord value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelIdempotencyInfoRecord}
   * @param {IdempotencyInfoRecord} fbeValue IdempotencyInfoRecord value
   */
  setFields (fbeValue) {
    this.Hash.set(fbeValue.Hash)
    this.Key.set(fbeValue.Key)
    this.Api.set(fbeValue.Api)
    this.Login.set(fbeValue.Login)
    this.ServerId.set(fbeValue.ServerId)
    this.RequestHash.set(fbeValue.RequestHash)
    this.ResponseBlob.set(fbeValue.ResponseBlob)
    this.ResponseFbeType.set(fbeValue.ResponseFbeType)
    this.State.set(fbeValue.State)
    this.Created.set(fbeValue.Created)
    this.Updated.set(fbeValue.Updated)
    this.TtlSeconds.set(fbeValue.TtlSeconds)
  }
}

export { FieldModelIdempotencyInfoRecord };

/**
 * Fast Binary Encoding IdempotencyInfoRecord model
 */
class IdempotencyInfoRecordModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelIdempotencyInfoRecord(this.buffer, 4)
  }

  /**
   * Get the IdempotencyInfoRecord model
   * @this {!IdempotencyInfoRecordModel}
   * @returns {!FieldModelIdempotencyInfoRecord} model IdempotencyInfoRecord model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!IdempotencyInfoRecordModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!IdempotencyInfoRecordModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return IdempotencyInfoRecordModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!IdempotencyInfoRecordModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelIdempotencyInfoRecord.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!IdempotencyInfoRecordModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!IdempotencyInfoRecordModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!IdempotencyInfoRecordModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!IdempotencyInfoRecordModel}
   * @param {!IdempotencyInfoRecord} value IdempotencyInfoRecord value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!IdempotencyInfoRecordModel}
   * @param {!IdempotencyInfoRecord} value IdempotencyInfoRecord value, defaults is new IdempotencyInfoRecord()
   * @return {!object} Deserialized IdempotencyInfoRecord value and its size
   */
  deserialize (value = new IdempotencyInfoRecord()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new IdempotencyInfoRecord(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new IdempotencyInfoRecord(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!IdempotencyInfoRecordModel}
   * @param {!number} prev Previous IdempotencyInfoRecord model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { IdempotencyInfoRecordModel };
